import React, { useEffect, useRef } from 'react';
import { AlertTriangleIcon, Trash2Icon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type RemoveEntryTypeDialogProps = {
  entryDescription: string;
  onClose: () => void;
  onConfirm: () => void;
};
export function RemoveEntryTypeDialog({
  entryDescription,
  onClose,
  onConfirm
}: RemoveEntryTypeDialogProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirmButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-481" data-mp="1m4jvswzgud7s">
      <button type="button" onClick={onClose} className="absolute inset-0 cursor-default bg-slate-900/45 backdrop-blur-sm" aria-label="Close remove entry type confirmation" data-id="element-482" data-mp="32j1vhek6mz7p" />
      <motion.section initial={{
      opacity: 0,
      scale: 0.97,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 10
    }} transition={{
      duration: 0.18,
      ease: 'easeOut'
    }} className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="remove-entry-type-title" aria-describedby="remove-entry-type-description" data-id="element-483" data-mp="1ccosbepf6lax">
        <header className="flex items-start justify-between border-b border-slate-100 p-5" data-id="element-484" data-mp="xp3t3vvo168a">
          <div className="flex items-start gap-3" data-id="element-485" data-mp="12mdmfmm6e21e">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600" data-id="element-486" data-mp="17e55lc3055hi">
              <AlertTriangleIcon className="h-5 w-5" data-id="element-487" data-mp="9z9h1gj67lp3" />
            </span>
            <div data-id="element-488" data-mp="exugveuig3d0">
              <h2 id="remove-entry-type-title" className="text-lg font-bold text-slate-900" data-id="element-489" data-mp="exaxrut4j8xe">
                Remove entry type?
              </h2>
              <p id="remove-entry-type-description" className="mt-1 text-sm text-slate-500" data-id="element-490" data-mp="2vt36oo7663n">
                This will remove the entry from this week and future weeks.
                Entries in previous weeks will remain unchanged.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Close remove entry type confirmation" data-id="element-491" data-mp="3k4pz3hcqi4hz">
            <XIcon className="h-5 w-5" data-id="element-492" data-mp="3p16kw4wgo6rw" />
          </button>
        </header>

        <div className="p-5" data-id="element-493" data-mp="19akbvejkav88">
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700" data-id="element-494" data-mp="199sgo4tvmzih">
            {entryDescription}
          </p>
        </div>

        <footer className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 p-5" data-id="element-495" data-mp="2m9du5ym6o6am">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-496" data-mp="2ratgdu03kiul">
            Keep entry type
          </button>
          <button ref={confirmButtonRef} type="button" onClick={onConfirm} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:ring-offset-2" data-id="element-497" data-mp="360ilu1f8390o">
            <Trash2Icon className="h-4 w-4" data-id="element-498" data-mp="3ovfpnc29io81" />
            Remove entry type
          </button>
        </footer>
      </motion.section>
    </div>;
}